/**
 * Pagination.
 *
 * Renders a WINDOW around the current page rather than every page. A result set
 * of 400 pages would otherwise emit 400 buttons — unusable, and a measurable
 * render cost on top of a list that is already the heaviest thing on screen.
 */

import { Button, cx } from '@/components/ui/primitives'

const WINDOW = 2

export function Pagination({
  page, pages, onChange,
}: {
  page: number
  pages: number
  onChange: (page: number) => void
}) {
  const numbers: number[] = []
  for (
    let index = Math.max(1, page - WINDOW);
    index <= Math.min(pages, page + WINDOW);
    index += 1
  ) {
    numbers.push(index)
  }

  return (
    <nav className="mt-6 flex flex-wrap items-center justify-center gap-1" aria-label="Pagination">
      <Button
        variant="secondary"
        size="sm"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
      >
        Previous
      </Button>

      {numbers[0] > 1 ? (
        <>
          <PageButton page={1} current={page} onChange={onChange} />
          {numbers[0] > 2 ? (
            <span className="px-1 text-xs text-[var(--text-tertiary)]">…</span>
          ) : null}
        </>
      ) : null}

      {numbers.map((number) => (
        <PageButton key={number} page={number} current={page} onChange={onChange} />
      ))}

      {numbers[numbers.length - 1] < pages ? (
        <>
          {numbers[numbers.length - 1] < pages - 1 ? (
            <span className="px-1 text-xs text-[var(--text-tertiary)]">…</span>
          ) : null}
          <PageButton page={pages} current={page} onChange={onChange} />
        </>
      ) : null}

      <Button
        variant="secondary"
        size="sm"
        disabled={page >= pages}
        onClick={() => onChange(page + 1)}
      >
        Next
      </Button>
    </nav>
  )
}

function PageButton({
  page, current, onChange,
}: {
  page: number
  current: number
  onChange: (page: number) => void
}) {
  const isCurrent = page === current
  return (
    <button
      type="button"
      onClick={() => onChange(page)}
      aria-current={isCurrent ? 'page' : undefined}
      className={cx(
        'size-8 rounded-md text-xs tabular transition-colors',
        isCurrent
          ? 'bg-[var(--accent)] font-medium text-white'
          : 'text-[var(--text-secondary)] hover:bg-[var(--surface-hover)]',
      )}
    >
      {page}
    </button>
  )
}
